/**
 * Copyright 2025 Lumavec Group LLC
 *
 * Dialog to display conflict information when a commit file revert fails
 */

import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import type { RevertCommitFileResult } from '../../../../server/types';

interface RevertCommitFileConflictDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  conflict: RevertCommitFileResult['conflicts'] | null;
}

export function RevertCommitFileConflictDialog({
  isOpen,
  onOpenChange,
  conflict
}: RevertCommitFileConflictDialogProps) {
  if (!conflict) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-yellow-500" size={20} />
            Cannot Automatically Revert
          </DialogTitle>
          <DialogDescription>
            The changes to <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">{conflict.filePath}</code> cannot be automatically reverted because the file has been modified since this commit.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-auto mt-4">
          <div className="text-sm text-gray-700 mb-4">
            You can manually revert these changes by editing the file directly. The conflicting changes are shown below:
          </div>
          
          <div className="bg-gray-900 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-gray-800 border-b border-gray-700 text-sm text-gray-300 font-medium">
              Conflicting Changes
            </div>
            <pre className="p-4 text-sm font-mono overflow-auto max-h-[400px] text-gray-100">
              {conflict.conflictLines && conflict.conflictLines.length > 0 ? (
                conflict.conflictLines.map((line, index) => (
                  <div
                    key={index}
                    className={`${
                      line.type === 'ours'
                        ? 'bg-green-900/30 text-green-300'
                        : line.type === 'theirs'
                        ? 'bg-red-900/30 text-red-300'
                        : line.type === 'marker'
                        ? 'bg-blue-900/30 text-blue-300'
                        : 'text-gray-400'
                    }`}
                  >
                    {line.content}
                  </div>
                ))
              ) : (
                <code className="whitespace-pre-wrap">{conflict.conflictContent}</code>
              )}
            </pre>
          </div>
          
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={16} />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">How to resolve manually:</p>
                <ol className="list-decimal list-inside space-y-1 text-yellow-700">
                  <li>Open the file in your editor</li>
                  <li>Review the changes shown above (lines starting with + were added, - were removed)</li>
                  <li>Manually undo the relevant changes</li>
                  <li>Save the file and commit when ready</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end pt-4 border-t">
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
